import { Copy, FileEdit } from 'lucide-react';
import { Button } from '../../../../components/ui/button.js';

export type MigrationAction = 'replace' | 'clone';

interface MigrationStartProps {
  onSelect: (action: MigrationAction) => void;
  /** When `true` the Replace button renders disabled. Today the
   *  surface is fully ported — the only reason to disable is when
   *  fewer than two Workfront workspaces are configured on the
   *  license (Replace requires source ≠ target). */
  replaceDisabled?: boolean;
  /** Optional explanation shown via the button's title attribute
   *  when `replaceDisabled` is `true`. */
  replaceDisabledReason?: string;
}

/**
 * Landing screen for the custom-forms migration page. Ports the
 * legacy `MigrationStart.tsx` minus the SVG illustration — the new
 * SPA uses lucide icons consistently.
 */
export function MigrationStart({
  onSelect,
  replaceDisabled = false,
  replaceDisabledReason,
}: MigrationStartProps): JSX.Element {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 px-4">
      <h1 className="text-3xl font-light text-foreground">Custom Forms Migration</h1>
      <p className="max-w-xl text-center text-sm text-foreground/60">
        Migrate Workfront custom forms — fields, field types, validations, and layout — between
        workspaces. <b>Clone</b> creates a brand-new form on the target; <b>Replace</b> overwrites
        an existing target form (and optionally backs it up first). Conditional-logic rules
        (show/hide, required-if) must be recreated manually; the Workfront API does not expose them.
      </p>
      <div className="flex gap-4">
        <Button
          variant="outline"
          size="lg"
          disabled={replaceDisabled}
          onClick={() => onSelect('replace')}
          title={replaceDisabled ? replaceDisabledReason : undefined}
        >
          <FileEdit className="mr-2 h-4 w-4" />
          Replace Custom Form
        </Button>
        <Button
          size="lg"
          onClick={() => onSelect('clone')}
          disabled={replaceDisabled}
          title={replaceDisabled ? replaceDisabledReason : undefined}
        >
          <Copy className="mr-2 h-4 w-4" />
          Clone Custom Form
        </Button>
      </div>
    </div>
  );
}
